/**
 * Strips `<ui_directive>…</ui_directive>` spans from live streaming text.
 *
 * The backend's directive_extractor (tricorder-go/chat/directive_extractor.go)
 * lifts these tags into typed Directives and strips them from the PERSISTED
 * assistant message — but raw streaming deltas reach the client before that
 * post-processor runs, so without this the nurse watches the raw JSON envelope
 * render inline for the rest of the stream (observed live on the twin's
 * platform-introduction answers, which emit navigate/spotlight directives).
 *
 * Tag literal mirrors the Go extractor's — producer change breaks both.
 */
const DIRECTIVE_OPEN = '<ui_directive>'
const DIRECTIVE_CLOSE = '</ui_directive>'

export function stripStreamingDirectiveTags(content: string): string {
  if (!content.includes('<')) return content

  // Complete spans: remove tag + payload entirely.
  let s = content.split(DIRECTIVE_OPEN).reduce((acc, part, i) => {
    if (i === 0) return part
    const close = part.indexOf(DIRECTIVE_CLOSE)
    // Opened but not yet closed (payload still streaming in): drop the tail.
    if (close === -1) return acc
    return acc + part.slice(close + DIRECTIVE_CLOSE.length)
  }, '')

  // A partially-typed opening tag at the very end ("<ui_dir…") would flash
  // before the span above can match — hide it until enough tokens arrive.
  // Minimum match length is 2 ("<u"): a lone trailing "<" is common in
  // ordinary clinical text (e.g. "K+ <", a lab-value comparison) and must
  // never be hidden just because it could theoretically start a directive.
  for (let k = Math.min(s.length, DIRECTIVE_OPEN.length - 1); k > 1; k--) {
    const tail = s.slice(-k)
    if (tail[0] === '<' && DIRECTIVE_OPEN.startsWith(tail)) {
      s = s.slice(0, -k)
      break
    }
  }
  return s
}
